import React, { useEffect } from 'react';
import type { ElementNode, LexicalNode } from 'lexical';
import {
  $getSelection,
  $isElementNode,
  $isLineBreakNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  COMMAND_PRIORITY_HIGH,
  KEY_ARROW_DOWN_COMMAND,
  KEY_ARROW_UP_COMMAND,
} from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $isInlineBoxNode, $isMentionNode } from './nodes';
import { getTextNodeCaretStartOffset } from './editor';

function isInlineDecoratorNode(node: LexicalNode | null | undefined): boolean {
  return $isInlineBoxNode(node) || $isMentionNode(node);
}

/** 在段落片段边界放置光标时，跳过原子节点后的零宽锚点前沿，保持 Safari 可见 caret 落在节点外部。 */
function placeCaretAtEndOfSegmentNode(node: LexicalNode): void {
  if ($isTextNode(node)) {
    const len = node.getTextContent().length;
    node.select(len, len);
    return;
  }
  if (isInlineDecoratorNode(node)) {
    const next = node.getNextSibling();
    if ($isTextNode(next)) {
      const startOffset = getTextNodeCaretStartOffset(next);
      next.select(startOffset, startOffset);
      return;
    }
    const parent = node.getParent();
    if ($isElementNode(parent)) {
      const children = parent.getChildren();
      const idx = children.indexOf(node);
      if (idx >= 0) {
        parent.select(idx + 1, idx + 1);
      }
    }
  }
}

/** 从段落内第 i 个子节点出发，向上移动到上一行末尾或段落开头 */
function moveUpFromChildIndex(paragraph: ElementNode, i: number): boolean {
  const children = paragraph.getChildren();
  if (i <= 0) {
    paragraph.selectStart();
    return true;
  }

  const prev = children[i - 1];
  if ($isLineBreakNode(prev)) {
    if (i === 1) {
      paragraph.selectStart();
      return true;
    }
    const end = i - 2;
    const last = children[end];
    placeCaretAtEndOfSegmentNode(last);
    return true;
  }

  if ($isTextNode(prev)) {
    const len = prev.getTextContent().length;
    prev.select(len, len);
    return true;
  }

  if (isInlineDecoratorNode(prev)) {
    const next = prev.getNextSibling();
    if ($isTextNode(next)) {
      const startOffset = getTextNodeCaretStartOffset(next);
      next.select(startOffset, startOffset);
    } else {
      paragraph.select(i, i);
    }
    return true;
  }

  return false;
}

/** 从段落内第 i 个子节点出发，向下移动到下一行开头或段落末尾 */
function moveDownFromChildIndex(paragraph: ElementNode, i: number): boolean {
  const children = paragraph.getChildren();
  let j = i + 1;
  while (j < children.length && !$isLineBreakNode(children[j])) {
    j += 1;
  }

  if (j >= children.length) {
    paragraph.selectEnd();
    return true;
  }

  const start = j + 1;
  if (start >= children.length) {
    paragraph.selectEnd();
    return true;
  }

  const first = children[start];
  if ($isTextNode(first)) {
    const startOffset = getTextNodeCaretStartOffset(first);
    first.select(startOffset, startOffset);
    return true;
  }
  if (isInlineDecoratorNode(first)) {
    paragraph.select(start, start);
    return true;
  }
  paragraph.select(start, start);
  return true;
}

/**
 * PlainText + 行内 Decorator 时，浏览器/Lexical 对上下键的纵向选区处理不完整；
 * 在 NodeSelection 选中 chip 或 Range 停在行内前缘时，补齐上下行跳转。
 */
export function DecoratorVerticalArrowPlugin({
  isComposingRef,
}: {
  isComposingRef: React.MutableRefObject<boolean>;
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    /**
     * 注意：命令处理器由 triggerCommandListeners 的 updateEditorSync 调用，已在 Lexical
     * 更新上下文中运行（editor._updating = true）。不要再包 editor.update()，否则回调
     * 会被推入队列延迟执行，导致 handled 永远为 false，event.preventDefault() 不会触发。
     */
    const handleUp = (event: KeyboardEvent | null) => {
      if (!event) return false;

      const selection = $getSelection();

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        const target = nodes.find((n) => isInlineDecoratorNode(n));
        if (!target) return false;

        const paragraph = target.getTopLevelElementOrThrow();
        const children = paragraph.getChildren();
        const i = children.indexOf(target);
        if (i < 0) return false;

        if (moveUpFromChildIndex(paragraph, i)) {
          event.preventDefault();
          return true;
        }
        return false;
      }

      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const { anchor } = selection;
        if (anchor.type !== 'element') return false;

        const el = anchor.getNode();
        if (!$isElementNode(el)) return false;

        const { offset: at } = anchor;
        const child = el.getChildAtIndex(at);
        if (!isInlineDecoratorNode(child)) return false;

        if (moveUpFromChildIndex(el, at)) {
          event.preventDefault();
          return true;
        }
      }

      return false;
    };

    const handleDown = (event: KeyboardEvent | null) => {
      if (!event) return false;

      const selection = $getSelection();

      if ($isNodeSelection(selection)) {
        const nodes = selection.getNodes();
        const target = nodes.find((n) => isInlineDecoratorNode(n));
        if (!target) return false;

        const paragraph = target.getTopLevelElementOrThrow();
        const children = paragraph.getChildren();
        const i = children.indexOf(target);
        if (i < 0) return false;

        if (moveDownFromChildIndex(paragraph, i)) {
          event.preventDefault();
          return true;
        }
        return false;
      }

      if ($isRangeSelection(selection) && selection.isCollapsed()) {
        const { anchor } = selection;
        if (anchor.type !== 'element') return false;

        const el = anchor.getNode();
        if (!$isElementNode(el)) return false;

        const { offset: at } = anchor;
        const child = el.getChildAtIndex(at);
        if (!isInlineDecoratorNode(child)) return false;

        if (moveDownFromChildIndex(el, at)) {
          event.preventDefault();
          return true;
        }
      }

      return false;
    };

    const unUp = editor.registerCommand(KEY_ARROW_UP_COMMAND, handleUp, COMMAND_PRIORITY_HIGH);
    const unDown = editor.registerCommand(KEY_ARROW_DOWN_COMMAND, handleDown, COMMAND_PRIORITY_HIGH);

    return () => {
      unUp();
      unDown();
    };
  }, [editor, isComposingRef]);

  return null;
}
